<template>
    <div class="packlist-body">
        <!-- Articles container with layout control -->
        <div class="articles-container" :class="{ 'grid-layout': layout === 'grid' }" :style="gridStyle">
            <ArticleCard 
                v-for="article in validArticles" 
                :key="article.id || `article-${article.sku}`" 
                :article="article" 
                :packlistItems="packlistItems"
                :layout="layout"
                :isDarkBackground="isDarkBackground"
                @reset-article="resetArticle"
                @toggle-background-mode="toggleGlobalBackgroundMode"
            />
        </div>

        <!-- Status Badge -->
        <div class="status-badge" :class="getBadgeClass" :style="badgePositionStyle" @click="handleBadgeClick">
            <div class="badge-content">
                <div class="badge-icon">
                    <v-icon v-if="isWarranty" color="white" size="18">mdi-alert-circle</v-icon>
                    <v-icon v-else-if="hasMissingItems" color="white" size="18">mdi-exclamation-thick</v-icon>
                    <v-icon v-else-if="isMulticart" color="white" size="18">mdi-cart-variant</v-icon>
                    <v-icon v-else color="white" size="18">mdi-package-variant</v-icon>
                </div>
                <div class="badge-text">
                    <div class="badge-count">{{ productsCount }}</div>
                    <div class="badge-label">{{ productsCount === 1 ? 'Artikel' : 'Artikel' }}</div>
                </div>
            </div>
            <div v-if="isWarranty || hasMissingItems || isMulticart" class="badge-status">
                <span v-if="isWarranty">Reklamation</span>
                <span v-else-if="hasMissingItems">Fehlende Artikel</span>
                <span v-else-if="isMulticart">Multicart</span>
            </div>
        </div>
    </div>
</template>

<script>
import ArticleCard from './ArticleCard.vue';

export default {
    name: 'PacklistBody',
    components: {
        ArticleCard
    },
    props: {
        articles: {
            type: Array,
            required: true
        },
        packlistItems: {
            type: Array,
            required: true
        },
        layout: {
            type: String,
            default: 'linear'
        },
        isMulticart: {
            type: Boolean,
            default: false
        },
        hasMissingItems: {
            type: Boolean,
            default: false
        },
        isDesktop: {
            type: Boolean,
            default: false
        },
        productsCount: {
            type: Number,
            default: 0
        },
        isWarranty: {
            type: Boolean,
            default: false
        },
        warrantyReason: {
            type: String,
            default: ''
        },
        currentOrderId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isDarkBackground: this.loadBackgroundSettings(),
            badgeUpdateTrigger: 0 // Counter to trigger badge position recalculation
        }
    },
    computed: {
        validArticles() {
            if (!Array.isArray(this.articles)) return [];
            return this.articles.filter(article => 
                article && 
                typeof article === 'object' && 
                (article.id || article.sku)
            );
        },
        dynamicColumns() {
            // Dynamic columns based on article count: 2 articles = 2 cols, 3 articles = 3 cols, max 3
            const articleCount = this.validArticles.length;
            if (articleCount <= 1) return 1;
            if (articleCount === 2) return 2;
            return 3; // 3 or more articles = 3 columns (max 3)
        },
        gridStyle() {
            if (this.layout !== 'grid') return {};
            return {
                'grid-template-columns': `repeat(${this.dynamicColumns}, minmax(0, 1fr))`
            };
        },
        badgePositionStyle() {
            // Include badgeUpdateTrigger to make this reactive to manual updates
            this.badgeUpdateTrigger; // Force reactivity dependency
            
            try {
                let distanceFromBottom = 8; // Base padding
                
                // Find the topmost bottom-positioned element and calculate distance from there
                let highestBottomElement = null;
                let highestPosition = 0;
                
                // Check packlist-actions-section
                const actionsSection = document.querySelector('.packlist-actions-section');
                if (actionsSection) {
                    const rect = actionsSection.getBoundingClientRect();
                    const elementTop = window.innerHeight - rect.bottom + rect.height; // Distance from bottom to top of element
                    if (elementTop > highestPosition) {
                        highestPosition = elementTop;
                        highestBottomElement = 'packlist-actions-section';
                    }
                    console.log(`📏 packlist-actions-section: height ${rect.height}px, top at ${elementTop}px from bottom`);
                }
                
                // Check package-size-selector
                const packageSelector = document.querySelector('.package-size-selector');
                if (packageSelector) {
                    const rect = packageSelector.getBoundingClientRect();
                    const elementTop = window.innerHeight - rect.bottom + rect.height; // Distance from bottom to top of element
                    if (elementTop > highestPosition) {
                        highestPosition = elementTop;
                        highestBottomElement = 'package-size-selector';
                    }
                    console.log(`📏 package-size-selector: height ${rect.height}px, top at ${elementTop}px from bottom`);
                }
                
                // Position badge 8px above the highest bottom element
                distanceFromBottom = highestPosition + 8;
                
                console.log(`🎯 Badge positioned ${distanceFromBottom}px from bottom (8px above ${highestBottomElement})`);
                
                // Sanity check
                if (distanceFromBottom < 20) {
                    distanceFromBottom = 130;
                    console.log(`⚠️ Distance too small, using fallback: ${distanceFromBottom}px`);
                }
                
                return {
                    bottom: `${distanceFromBottom}px`
                };
                
            } catch (error) {
                console.error('❌ Badge positioning error:', error);
                return { bottom: '130px' };
            }
        },
        getBadgeClass() {
            // Priority order: warranty (orange) > missing items (red) > multicart (purple) > normal (green)
            if (this.isWarranty) {
                console.log('🟠 Badge class: badge-warning (warranty)');
                return 'badge-warning';
            } else if (this.hasMissingItems) {
                console.log('🔴 Badge class: badge-missing (missing items)');
                return 'badge-missing';
            } else if (this.isMulticart) {
                console.log('🟣 Badge class: badge-multicart (multicart)');
                return 'badge-multicart';
            } else {
                console.log('🟢 Badge class: badge-normal (normal green)');
                console.log('🟢 Articles count for badge:', this.productsCount);
                return 'badge-normal';
            }
        }
    },
    watch: {
        articles: {
            handler(newVal, oldVal) {
                console.log('PacklistBody - articles prop changed:', newVal);
                console.log('PacklistBody - articles count:', newVal.length);
                console.log('PacklistBody - dynamic columns:', this.dynamicColumns);
                
                // This indicates currentOrderId or currentPacklistIndex changed
                if (oldVal && newVal !== oldVal) {
                    console.log('🔄 Item switched - updating badge position');
                    setTimeout(() => {
                        this.badgeUpdateTrigger++;
                    }, 200); // Longer delay to ensure all DOM updates complete
                }
            },
            immediate: true
        },
        dynamicColumns: {
            handler(newVal) {
                console.log('PacklistBody - grid columns changed to:', newVal);
            }
        },
        isMulticart: {
            handler() {
                // Trigger badge position update when multicart state changes
                console.log('🟣 Multicart state changed - updating badge position');
                this.badgeUpdateTrigger++;
            }
        },
        hasMissingItems: {
            handler() {
                // Trigger badge position update when missing items state changes
                console.log('🔴 Missing items state changed - updating badge position');
                this.badgeUpdateTrigger++;
            }
        },
        isWarranty: {
            handler() {
                // Trigger badge position update when warranty state changes
                console.log('🟠 Warranty state changed - updating badge position');
                this.badgeUpdateTrigger++;
            }
        },
        packlistItems: {
            handler() {
                // Trigger badge position update when packlist items change (item switching)
                this.$nextTick(() => {
                    setTimeout(() => {
                        console.log('📦 Packlist items changed - updating badge position');
                        this.badgeUpdateTrigger++;
                    }, 100); // Small delay to let DOM update
                });
            },
            deep: true
        },
        productsCount: {
            handler() {
                // Trigger badge update when product count changes (indicates item switch)
                console.log('📊 Products count changed - updating badge position');
                setTimeout(() => {
                    this.badgeUpdateTrigger++;
                }, 150); // Delay to ensure DOM is updated
            }
        },
        warrantyReason: {
            handler() {
                // Trigger badge update when warranty reason changes (indicates item switch)
                console.log('📝 Warranty reason changed - updating badge position');
                setTimeout(() => {
                    this.badgeUpdateTrigger++;
                }, 150); // Delay to ensure DOM is updated
            }
        },
        currentOrderId: {
            handler(newVal, oldVal) {
                // This is the primary indicator of item switching
                if (oldVal !== undefined && newVal !== oldVal) {
                    console.log(`🔄 ITEM SWITCHED: ${oldVal} → ${newVal}`);
                    console.log('📍 Triggering badge position recalculation for new item');
                    
                    // Use multiple delays to catch different timing scenarios
                    setTimeout(() => {
                        console.log('📍 Badge recalc attempt 1 (100ms delay)');
                        this.badgeUpdateTrigger++;
                    }, 100);
                    
                    setTimeout(() => {
                        console.log('📍 Badge recalc attempt 2 (300ms delay)');
                        this.badgeUpdateTrigger++;
                    }, 300);
                    
                    setTimeout(() => {
                        console.log('📍 Badge recalc attempt 3 (500ms delay)');
                        this.badgeUpdateTrigger++;
                    }, 500);
                }
            }
        }
    },
    methods: {
        resetArticle(article) {
            this.$emit('reset-article', article);
        },
        toggleGlobalBackgroundMode() {
            this.isDarkBackground = !this.isDarkBackground;

            let backgroundSettings = {
                isDarkBackground: this.isDarkBackground
            }

            localStorage.setItem('backgroundSettings', JSON.stringify(backgroundSettings));
        },
        loadBackgroundSettings() {
            let backgroundSettings = localStorage.getItem('backgroundSettings');

            if (!backgroundSettings) {
                let newBackgroundSettings = {
                    isDarkBackground: false
                }

                localStorage.setItem('backgroundSettings', JSON.stringify(newBackgroundSettings));
                return false;
            } else {
                return JSON.parse(backgroundSettings).isDarkBackground;
            }
        },
        triggerBadgePositionUpdate() {
            // Manual trigger for badge position updates
            console.log('🔄 Manually triggering badge position update');
            this.badgeUpdateTrigger++;
        },
        handleBadgeClick() {
            // Open warranty modal if badge is for warranty
            if (this.isWarranty) {
                console.log('🟠 Warranty badge clicked - opening warranty modal');
                this.$emit('open-warranty-modal');
            } else {
                console.log('ℹ️ Badge clicked but not a warranty item');
            }
        }
    },
    mounted() {
        // Trigger badge position calculation after DOM is ready
        this.$nextTick(() => {
            setTimeout(() => {
                console.log('🚀 PacklistBody mounted - triggering badge position calculation');
                this.triggerBadgePositionUpdate();
            }, 500); // Small delay to ensure all components are rendered
        });
    },
    updated() {
        // Component updated - avoid infinite loops, let computed properties handle reactivity
        console.log('🔄 PacklistBody updated');
    }
}
</script>

<style>
.packlist-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    height: 100%;
    min-height: 0; /* Critical for proper flex behavior */
    overflow-y: auto; /* Changed from scroll to auto for better compatibility */
    box-sizing: border-box; /* Include padding in height calculation */
    flex: 1; /* Ensure it fills available space */
    width: 100%; /* Ensure full width */
}

.articles-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
}

.articles-container.grid-layout {
    display: grid;
    /* grid-template-columns now set dynamically via :style */
    grid-auto-rows: auto; /* Let rows grow to fit content */
    gap: 16px;
    overflow-x: hidden; /* Prevent horizontal scrolling */
    box-sizing: border-box;
    align-items: start; /* Align cards to top of grid cells */
}

/* Adjust gap for 3-column layouts */
.articles-container.grid-layout {
    gap: clamp(12px, 2vw, 16px); /* Responsive gap */
}

/* Ensure cards fit properly within grid cells */
.articles-container.grid-layout .article-card {
    width: 100%;
    max-width: 100%;
    height: auto; /* Let cards grow with their content */
    min-width: 0; /* Allow shrinking below intrinsic size */
    box-sizing: border-box;
    word-wrap: break-word; /* Handle long text */
    overflow-wrap: break-word;
    display: flex;
    flex-direction: column; /* Stack header, image, details vertically */
}

.multicart-warning {
    background-color: #8700cb;
    color: white;
    padding: 16px;
    border-radius: 8px;
}

.missing-items-warning {
    background-color: #f44336;
    color: white;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
}

.missing-items-warning::before {
    content: "⚠️";
    font-size: 1.2em;
}

.warranty-warning {
    background-color: #ff9800;
    color: white;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
}

.warranty-warning::before {
    content: "⚠️";
    font-size: 1.2em;
}

/* Status Badge */
.status-badge {
    position: fixed;
    right: 20px;
    /* bottom position set dynamically via badgePositionStyle */
    background: linear-gradient(135deg, #4caf50, #45a049);
    border-radius: 16px;
    padding: 12px 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    min-width: 120px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
}

/* Add hover effect for warranty badges */
.status-badge.badge-warning {
    cursor: pointer;
}

.status-badge.badge-warning:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 20px rgba(255, 152, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.2);
}

.status-badge:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2), 0 3px 6px rgba(0, 0, 0, 0.15);
}

/* Badge color variants - higher specificity to ensure proper colors */
.status-badge.badge-normal {
    background: linear-gradient(135deg, #4caf50, #45a049) !important;
}

.status-badge.badge-warning {
    background: linear-gradient(135deg, #ff9800, #f57f17) !important;
}

.status-badge.badge-multicart {
    background: linear-gradient(135deg, #8700cb, #6a0080) !important;
}

.status-badge.badge-missing {
    background: linear-gradient(135deg, #f44336, #d32f2f) !important;
}

.badge-content {
    display: flex;
    align-items: center;
    gap: 10px;
}

.badge-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    flex-shrink: 0;
}

.badge-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
}

.badge-count {
    font-size: 18px;
    font-weight: 700;
    color: white;
    margin: 0;
}

.badge-label {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 1px;
}

.badge-status {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    text-align: center;
}

.badge-status span {
    font-size: 10px;
    font-weight: 600;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.95;
}

/* Mobile responsive adjustments */
@media (max-width: 767px) {
    .status-badge {
        right: 16px;
        padding: 10px 12px;
        min-width: 100px;
    }

    .badge-count {
        font-size: 16px;
    }

    .badge-label {
        font-size: 10px;
    }
}
</style>